import React from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment-timezone';
import numeral from 'numeral';
import { Link } from '../../shared/link';
import { MakeApiCall, WriteConsoleErrors, GetMomentTimeZoneName, GoBackOrRoot } from '../../shared/utils';
import { ErrorsBlockComponent } from '../../shared/errorsBlockComponent';
import { RefreshButtonComponent } from '../../shared/refreshButtonComponent';
import { InfoItem } from '../AccountInfo/infoitem';
import { DateTimeItem } from '../AccountInfo/datetimeitem';
import { UrlBuilder } from '../../app.defs';
import { PageHeader } from '../../shared/PageHeader';
import { Checkbox } from '../../shared/checkbox';
import { ButtonBusyIndicatorComponent } from '../../shared/buttonBusyIndicatorComponent';
import { BaseFormComponent } from  '../../shared/baseFormComponent';
import { LoadingIndicatorComponent } from '../../shared/loadingIndicatorComponent';
import { DateTimePicker } from '../../shared/TimePicker';

const AFFILIATEPAYOUTINFO_ACTION = {
    NONE: 0,
    LOADING: 1,
    SAVING: 2,
}

const commissionStyle = {
    maxWidth: '8.5em'
}

export class AffiliatePayoutInfoComponent extends BaseFormComponent {
    constructor(props, ...others) {
        super(props, ...others);

        this.state = {
            isLoaded: false,
            action: AFFILIATEPAYOUTINFO_ACTION.NONE,
            errors: null,
            formData: null,

            payoutId: props.payoutId,
            affiliateId: props.affiliateId,
        }

        this.doLoad = this.doLoad.bind(this);
        this.doSave = this.doSave.bind(this);
        this.doCancel = this.doCancel.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleDateTimeChange = this.handleDateTimeChange.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }

        const data = {PayoutId: self.state.payoutId, AffiliateId: self.state.affiliateId};

        new Promise(resolve => {
            self.setState({action: AFFILIATEPAYOUTINFO_ACTION.LOADING, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAffiliatePayoutInfo',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                );
            }
        })
        .then(response => {
            if (self._isMounted) {
                self.setState({
                    action: AFFILIATEPAYOUTINFO_ACTION.NONE,
                    isLoaded: true,
                    formData: response
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted) {
                self.setState({action: AFFILIATEPAYOUTINFO_ACTION.NONE, errors: errors});
            }
        });
    }

    doSave() {
        const self = this;
        const {formData} = self.state;

        const data = {...formData};

        const verificationToken = formData.VerificationToken;

        if (!Boolean(data.Id)) {
            data.Id = null;
        }

        new Promise(resolve => {
            self.setState({action: AFFILIATEPAYOUTINFO_ACTION.SAVING, errors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.post(
                        {
                            url: '/api/admin/saveAffiliatePayout',
                            headers: {RequestVerificationToken: verificationToken},
                            data: JSON.stringify(data),
                            contentType: 'application/json',
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false
                )
            }
        })
        .then(() => {
            if (self._isMounted) {
                self.doCancel();
                // self.setState({
                //     action: AFFILIATEPAYOUTINFO_ACTION.NONE,
                //     isLoaded: true,
                //     formData: response
                // });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted) {
                self.setState({action: AFFILIATEPAYOUTINFO_ACTION.NONE, errors: errors});
            }
        });
    }

    doCancel() {
        if (this._isMounted) {
            GoBackOrRoot();
        }
    }

    handleSubmit(event) {
        event.preventDefault();
        this.doSave();
    }

    handleDateTimeChange(name, value) {
        const self = this;
        const formData = {...self.state.formData};

        formData[name] = moment.utc(value);

        self.setState({formData: formData});
    }

    handleCancelClick() {
        this.doCancel();
    }

    render() {
        const self = this;
        const {formData, action, isLoaded, errors} = self.state;
        const {timeZoneId} = self.props;

        let payoutId = self.props.payoutId || formData?.Id;

        const isDisabled = action != AFFILIATEPAYOUTINFO_ACTION.NONE;
        let paidAt = null;
        let periodFrom = null;
        let periodTo = null;

        if (isLoaded) {
            paidAt = moment.utc(formData.PaidAt).isValid() 
                ? moment.utc(formData.PaidAt).tz(timeZoneId)
                : formData.PaidAt;

            periodFrom = moment.utc(formData.PeriodFrom).isValid() 
                ? moment.utc(formData.PeriodFrom)
                : formData.PeriodFrom;

            periodTo = moment.utc(formData.PeriodTo).isValid() 
                ? moment.utc(formData.PeriodTo)
                : formData.PeriodTo;
        }

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={75}
                    caption={
                        <h2 className="text-truncate">
                            {Boolean(payoutId) && <span className="mr-2">Affiliate Payout #{payoutId}</span>}
                            {!Boolean(payoutId) && <span className="mr-2">New Affiliate Payout</span>}
                        </h2>
                    }
                />
                <ErrorsBlockComponent errors={errors} />
                {isLoaded &&
                    <div>
                        <div className="form-group">
                            <InfoItem 
                                caption="Affiliate" 
                                content={
                                    <>
                                        <span className="mr-2">{formData.AffiliateId}</span>
                                        <Link
                                            to={formData.AffiliateAdminUrl}
                                            disabled={isDisabled}
                                            target="_blank"
                                        >
                                            {formData.AffiliateName}
                                        </Link>
                                    </>
                                } 
                            />
                        </div>
                        <form onSubmit={self.handleSubmit}>
                            <div className="row">
                                <div className="col-sm-8 col-md-6 col-lg-5 col-xl-4">
                                    <div className="form-group newstyle">
                                        <DateTimePicker
                                            inputProps={{className: 'custom-select w-100', id: 'PaidAt'}}
                                            disabled={isDisabled}
                                            value={paidAt}
                                            onChange={value => self.handleDateTimeChange('PaidAt', moment.utc(value))}
                                            utc
                                        />
                                        <label htmlFor="PaidAt">
                                            <span className="text-truncate">
                                                Payment time
                                                <span className="ml-1 small text-secondary">{GetMomentTimeZoneName(timeZoneId, GetMomentTimeZoneName.FLAG_ADD_UTCOFFSET, '')}</span>
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-group newstyle">
                                        <DateTimePicker
                                            inputProps={{className: 'custom-select w-100', id: 'PeriodFrom'}}
                                            disabled={isDisabled}
                                            value={periodFrom}
                                            onChange={value => self.handleDateTimeChange('PeriodFrom', moment.utc(value))}
                                            utc
                                        />
                                        <label htmlFor="PeriodFrom">
                                            <span className="text-truncate">
                                                Period from
                                                <span className="ml-1 small text-secondary">(UTC)</span>
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-group newstyle">
                                        <DateTimePicker
                                            inputProps={{className: 'custom-select w-100', id: 'PeriodTo'}}
                                            disabled={isDisabled}
                                            value={periodTo}
                                            onChange={value => self.handleDateTimeChange('PeriodTo', moment.utc(value))}
                                            utc
                                        />
                                        <label htmlFor="PeriodTo">
                                            <span className="text-truncate">
                                                Period to
                                                <span className="ml-1 small text-secondary">(UTC)</span>
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-group newstyle">
                                        <input
                                            id="Commission"
                                            name="Commission"
                                            type="number"
                                            className={self.getControlClass('Commission', errors, 'form-control text-right')}
                                            disabled={isDisabled}
                                            value={formData.Commission}
                                            onChange={self.handleInputChange}
                                            onBlur={self.handleInputChange}
                                            style={commissionStyle}
                                        />
                                        <label htmlFor="Commission"><span className="text-truncate">Commission, $</span></label>
                                        <div className="invalid-feedback">{self.getControlError('Commission', errors)}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-10 col-lg-7 col-xl-6">
                                    <div className="form-group newstyle">
                                        <textarea
                                            id="Notes"
                                            name="Notes"
                                            className="form-control mw-100"
                                            rows={3}
                                            disabled={isDisabled}
                                            value={formData.Notes || ''}
                                            onChange={self.handleInputChange}
                                        />
                                        <label htmlFor="Notes"><span className="text-truncate">Notes</span></label>
                                    </div>
                                </div>
                            </div>

                            <div className="form-buttons pt-3">
                                <button
                                    type="submit"
                                    disabled={isDisabled}
                                    className="btn btn-sm btn-primary newstyle"
                                >
                                    {Boolean(payoutId) && <span>Save</span>}
                                    {!Boolean(payoutId) && <span>Add payout</span>}
                                    <ButtonBusyIndicatorComponent visible={action == AFFILIATEPAYOUTINFO_ACTION.SAVING} />
                                </button>
                                <button
                                    type="button"
                                    disabled={isDisabled}
                                    className="btn btn-sm btn-outline-secondary newstyle"
                                    onClick={self.handleCancelClick}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                }
            </div>
        )
    }
}